import {Icon} from '../ui/Icon';
import {WorkspaceRegisterOverview,hasWorkspaceRegisterOverview} from './WorkspaceRegisterOverview';
import {OperationalAttention} from './OperationalAttention';
import {PortfolioComplianceOverview} from './PortfolioComplianceOverview';
import {PortfolioFinanceOverview} from './PortfolioFinanceOverview';
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import { OutcomeOversight } from './OutcomeOversight';
import { api } from '../api';
import { Loading, ErrorState } from '../ui/State';
import { useWorkspaceSession } from '../ui/StaffSession';
import { workspaceGroups,workspaceDescriptions,permittedWorkspaces } from '../ui/workspace-catalogue';
import { workspaceRegisters } from '../ui/area-navigation';
import { WorkspaceAttention } from './WorkspaceAttention';
import { PageHeader } from '../ui/PageHeader';
import {LazyDetails} from '../ui/LazyDetails';
import { withBuildingScope } from '../ui/building-scope';
import { formatDateOnly } from '../dates';
import './engine-workspaces.css';
type Contact = { name: string; startsOn: string; endsOn: string | null };
type Workspace = {
  code: string;
  name: string;
  coverage: string;
  ownership: { ownershipStatus: string; primary: Contact | null; acting: (Contact & { decision: string; availability: string })[] };
  destinations: { label: string; available: boolean; href?: string; reason?: string }[];
  buildings: { id: string; name: string }[];
  manageHref?: string;
  administrators?: { accountId: string; name: string; endsOn: string | null }[];
};
type Entry = { code: string; name: string; available: boolean; href?: string };
export function WorkspaceWork(){
  const session=useWorkspaceSession()!,{code}=useParams();
  if(code==='operations_compliance'&&session.capabilities.includes('field-reports:view'))return <OperationalAttention/>;
  return code&&session.capabilities.includes('contacts:view')&&session.ownership?.engineCodes.includes(code)?<OutcomeOversight/>:<EngineWorkspaces/>;
}
export function EngineWorkspaces() {
  const session=useWorkspaceSession()!;
  const { code } = useParams(),
    [params] = useSearchParams(),
    building = params.get('building') ?? undefined;
  const location=useLocation(),tab=location.pathname.endsWith('/contributions')?'work':params.get('tab')??'overview',registers=code?workspaceRegisters(session,code):[];
  const queues=registers.filter(item=>item.group==='Review queues');
  const [directory, setDirectory] = useState<Entry[]>(),
    [workspace, setWorkspace] = useState<Workspace>(),
    [error, setError] = useState<unknown>();
  useEffect(() => {
    const controller = new AbortController();
    setError(undefined);
    setWorkspace(undefined);
    setDirectory(undefined);
    const path = code ? '/api/engine-workspaces/' + encodeURIComponent(code) + (building ? '?building=' + encodeURIComponent(building) : '') : '/api/engine-workspaces';
    void api<Workspace | { engines: Entry[] }>(path, { signal: controller.signal })
      .then((r) => {
        if (controller.signal.aborted) return;
        if ('engines' in r) setDirectory(r.engines);
        else setWorkspace(r);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e);
      });

  return () => controller.abort();
  }, [code, building]);
  const queueLabels:Record<string,string>={new_business:'Open lead pipeline',onboarding:'Open onboarding queue',property_admin:'Open people register',repairs:'Open work queue',operations_compliance:'Open operations queue',suppliers:'Open supplier register',finance:'Open finance workspace',communications:'Open Mail',
 governance:'Open governance records',insurance:'Open policy register',assets:'Open asset register',people:'Open people directory'};
 const utility=(<nav className="workspace-utility-links" aria-label="Workspace context">
              <Link to={withBuildingScope('/engines',building)}>All workspaces</Link>
              {building&&<Link to={withBuildingScope('/buildings/'+building+'/outcomes',building)}>Building outcomes and handoffs</Link>}
              <Link to={withBuildingScope('/engines/'+code+'/standards',building)}>Standards and charter</Link>
            </nav>);
  return (
    <div className="engine-workspaces">
      <PageHeader title={code ? (workspace?.name ?? 'Workspace') : 'Workspaces'} context={code?(workspaceDescriptions[code]??''):'One place for each service. Open a workspace to see its work, records and next decisions.'}
 primaryAction={workspace?.destinations.find(item=>item.available&&item.href)?.href?{kind:'link',icon:'arrow-right',label:queueLabels[code??'']??'Open work queue',to:workspace.destinations.find(item=>item.available&&item.href)!.href!}:undefined}/>
      {error !== undefined ? (
        <ErrorState error={error} />
      ) : !workspace && !directory ? (
        <Loading />
      ) : directory ? (
        <>
          {workspaceGroups.map(group=><section className="workspace-directory-group" key={group.label}><h2>{group.label}</h2><ul className="engine-directory">{group.codes.map(code=>directory.find(item=>item.code===code)).filter(e=>e!==undefined).map(e=><li key={e.code}>
            {e.available&&e.href?<Link to={permittedWorkspaces(session).find(item=>item.code===e.code)?.href??e.href}><span>{e.name}</span><small>{workspaceDescriptions[e.code]}</small><span className="workspace-open">Open workspace <Icon name="arrow-right"/></span></Link>:<><strong>{e.name}</strong><small>Unavailable with your current access</small></>}
          </li>)}</ul></section>)}
        </>
      ) : (
        workspace && (
          <>
            {tab!=='overview'&&utility}
            {tab==='overview'&&code==='operations_compliance'&&session.capabilities.includes('compliance:view')&&<PortfolioComplianceOverview/>}
            {tab==='overview'&&code==='finance'&&session.capabilities.includes('finance:view')&&<PortfolioFinanceOverview/>}
            {tab==='overview'&&code&&<WorkspaceRegisterOverview code={code}/>}
            {tab==='overview'&&hasWorkspaceRegisterOverview(workspace.code,session.capabilities)&&session.capabilities.includes('contacts:view')&&session.ownership?.engineCodes.includes(workspace.code)&&<LazyDetails summary="Additional assigned work and handoffs" className="workspace-additional-work"><WorkspaceAttention code={workspace.code} building={building}/></LazyDetails>}
            {tab==='overview'&&!hasWorkspaceRegisterOverview(workspace.code,session.capabilities)&&(session.capabilities.includes('contacts:view')&&session.ownership?.engineCodes.includes(workspace.code)
              ?<WorkspaceAttention code={workspace.code} building={building}/>
              :<section><h2>Work and handoffs</h2><p>The consolidated work view requires a current engine delivery appointment. You can open the registers below with your existing access.</p></section>)}
            {tab==='overview'&&<details className="workspace-information"><summary>Workspace information</summary>{utility}
            <section aria-labelledby="engine-ownership">
              <h2 id="engine-ownership">Accountable Engine Manager</h2>
              <p>{workspace.ownership.primary?.name ?? workspace.ownership.ownershipStatus}</p>
              {workspace.ownership.primary && (
                <p>
                  {formatDateOnly(workspace.ownership.primary.startsOn)} to {workspace.ownership.primary.endsOn ? formatDateOnly(workspace.ownership.primary.endsOn) : 'ongoing'}
                  {workspace.ownership.primary.endsOn ? ' (end date exclusive)' : ''} · {workspace.ownership.ownershipStatus}
                </p>
              )}
              {workspace.ownership.acting.length > 0 && (
                <>
                  <h3>Acting responsibility</h3>
                  <ul>
                    {workspace.ownership.acting.map((a, i) => (
                      <li key={i}>
                        {a.name} · {a.decision === 'engine_technical_review' ? 'Technical review' : 'Engine delivery'} · {formatDateOnly(a.startsOn)} to {formatDateOnly(a.endsOn)}{' '}
                        · {a.availability}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {workspace.manageHref && <Link to={workspace.manageHref}>Manage engine ownership</Link>}
            </section>
            {workspace.administrators && <section aria-labelledby="engine-administrators">
              <h2 id="engine-administrators">Engine administrators</h2>
              <ul>{workspace.administrators.map(a=><li key={a.accountId}>{a.name} · {a.endsOn ? 'Until '+formatDateOnly(a.endsOn) : 'Until changed'}</li>)}</ul>
              <p>Shared administration supports holiday and sickness cover. The accountable manager remains recorded separately.</p>
            </section>}
            </details>}
            <details className="workspace-register-directory" open={tab!=='overview'}><summary>{tab==='overview'?'Registers and queues':'Original work registers'}</summary><section aria-labelledby="engine-registers">
              <h2 id="engine-registers">{tab==='review'?'Review queues':tab==='work'?'Work in this workspace':'Registers'}</h2>
              {tab==='work'&&<p>Open the original work records below. The consolidated handoff view requires a current engine delivery appointment.</p>}
              {tab==='review'?queues.length?<ul className="workspace-registers">{queues.map(d=><li key={d.href}><Link to={d.href}>{d.label}<Icon name="arrow-right"/></Link></li>)}</ul>:<p>This workspace has no separate review queue. Review decisions in the original work records.</p>:<ul className="workspace-registers">
                {[...workspace.destinations,...registers.filter(d=>d.group!=='Review queues').map(d=>({label:d.label,available:true,href:d.href}))].filter((d,i,all)=>!d.href||all.findIndex(other=>other.href===d.href)===i).map(d=>
<li key={d.label}>{d.available&&d.href?<Link to={d.href}>{d.label}<Icon name="arrow-right"/>
</Link>:<span>{d.label} — {'reason' in d?d.reason:'Choose a building'}</span>}</li>)}
              </ul>}
            </section></details>
          </>
        )
      )}
    </div>
  );
}
